import { ReactNode } from "react";

import { PercentageOutlined, SettingOutlined, CalendarOutlined } from "@ant-design/icons";

import {
    UserIcon,
    Building,
    // Home,
    ClipboardList,
    ListChecks,
    BarChart3,
    Gift,
    Users,
    Briefcase,
    FileText,
    MapPin,
    HardHat,
    CreditCard,
    ShoppingCart,
    Truck,
    Wallet,
    BarChart2,
    Key,
    WalletCards,
    BookOpen,
    DollarSign,
    Scale,
    Landmark,
    KeyRound,
    MessageSquareText,
} from "lucide-react";
import { AccountType, UserRole } from "@/types";
import { routes } from "./routes";

export type CustomMenuItem = {
    key: string;
    icon?: ReactNode;
    label: ReactNode;
    path?: string;
    allowedRoles?: string[];
    allowedAccountTypes?: AccountType[];
    children?: CustomMenuItem[];
};

const commonNavItems: CustomMenuItem[] = [
    {
        key: 'customer-feedback',
        label: 'Customer feedback',
        path: '/customer-feedback',
        icon: <span className="anticon"><MessageSquareText /></span>,
        allowedAccountTypes: [AccountType.INTERNAL],
    },
    // {
    //     key: "dashboard",
    //     label: "Dashboard",
    //     path: routes.dashboard.root,
    //     icon: (
    //         <span className="anticon">
    //             <Home />
    //         </span>
    //     ),
    //     allowedRoles: [
    //         UserRole.SUPER_ADMIN,
    //         UserRole.CHANNEL_ADMIN,
    //         UserRole.CHANNEL_SALES_REP,
    //     ],
    // },
    {
        label: "Leads",
        path: "/leads",
        key: "leads",
        icon: (
            <span className="anticon">
                <ListChecks />
            </span>
        ),
        allowedRoles: [
            UserRole.SUPER_ADMIN,
            UserRole.CHANNEL_ADMIN,
            UserRole.CHANNEL_SALES_REP,
            UserRole.BRAND_AMBASSADOR,
        ],
    },
    {
        key: "opportunities",
        label: "Opportunities",
        path: "/opportunities",
        icon: (
            <span className="anticon">
                <BarChart3 />
            </span>
        ),
        allowedRoles: [
            UserRole.SUPER_ADMIN,
            UserRole.CHANNEL_ADMIN,
            UserRole.CHANNEL_SALES_REP,
            UserRole.BRAND_AMBASSADOR,
        ],
    },
    {
        key: "contacts",
        label: "Contacts",
        path: "/contacts",
        icon: (
            <span className="anticon">
                <Users />
            </span>
        ),
        allowedRoles: [UserRole.SUPER_ADMIN, UserRole.CHANNEL_ADMIN, UserRole.BRAND_AMBASSADOR, UserRole.CHANNEL_SALES_REP],
    },
    {
        key: "customer-accounts",
        label: "Customer Accounts",
        path: "/customer-accounts",
        icon: (
            <span className="anticon">
                <Briefcase />
            </span>
        ),
        allowedRoles: [
            UserRole.SUPER_ADMIN,
            UserRole.CHANNEL_ADMIN,
            UserRole.CHANNEL_SALES_REP,
            UserRole.BRAND_AMBASSADOR,
        ],
    },
    {
        key: "quotes",
        label: "Quotes",
        path: routes.quotes.root,
        icon: (
            <span className="anticon">
                <FileText />
            </span>
        ),
        allowedRoles: [
            UserRole.SUPER_ADMIN,
            UserRole.CHANNEL_ADMIN,
            UserRole.CHANNEL_SALES_REP,
        ],
        allowedAccountTypes: [AccountType.CHANNEL_PARTNER, AccountType.INTERNAL],
    },
    {
        key: "proposals",
        label: "Proposals",
        path: routes.proposals.root,
        icon: (
            <span className="anticon">
                <FileText />
            </span>
        ),
        allowedRoles: [
            UserRole.SUPER_ADMIN,
            UserRole.CHANNEL_ADMIN,
            UserRole.CHANNEL_SALES_REP,
        ],
        allowedAccountTypes: [AccountType.INTERNAL],
    },
    {
        key: "products",
        label: "Products",
        path: routes.products.root,
        icon: (
            <span className="anticon">
                <Briefcase />
            </span>
        ),
        allowedRoles: [
            UserRole.SUPER_ADMIN,
            UserRole.CHANNEL_ADMIN,
            UserRole.CHANNEL_SALES_REP,
        ],
        allowedAccountTypes: [AccountType.CHANNEL_PARTNER, AccountType.INTERNAL],
    },
    {
        key: "orders",
        label: "Orders",
        path: routes.orders.root,
        icon: (
            <span className="anticon">
                <ShoppingCart />
            </span>
        ),
        allowedRoles: [
            UserRole.SUPER_ADMIN,
            UserRole.CHANNEL_ADMIN,
            UserRole.CHANNEL_SALES_REP,
        ],
        allowedAccountTypes: [AccountType.CHANNEL_PARTNER, AccountType.INTERNAL],
    },
    {
        key: "documents",
        label: "Partner Resources",
        path: routes.documents.root,
        icon: (
            <span className="anticon">
                <FileText />
            </span>
        ),
        allowedRoles: [
            UserRole.SUPER_ADMIN,
            UserRole.CHANNEL_ADMIN,
            UserRole.CHANNEL_SALES_REP,
            UserRole.BRAND_AMBASSADOR,
        ],
        allowedAccountTypes: [AccountType.CHANNEL_PARTNER, AccountType.BRAND_AMBASSADOR, AccountType.INTERNAL],
    },
];

// Regular admin tools
const settingsItems: CustomMenuItem[] = [
    {
        key: "settings",
        label: "Settings",
        icon: <SettingOutlined />,
        children: [
            {
                key: "users",
                label: "Users",
                path: routes.settings.users.root,
                icon: (
                    <span className="anticon">
                        <UserIcon />
                    </span>
                ),
                allowedRoles: [
                    UserRole.SUPER_ADMIN,
                    UserRole.CHANNEL_ADMIN,
                    UserRole.BRAND_AMBASSADOR,
                ],
            },
            {
                key: "addresses",
                label: "Addresses",
                path: routes.settings.addresses.root,
                icon: (
                    <span className="anticon">
                        <MapPin />
                    </span>
                ),
                allowedAccountTypes: [AccountType.CHANNEL_PARTNER, AccountType.INTERNAL],
                allowedRoles: [
                    UserRole.SUPER_ADMIN,
                    UserRole.CHANNEL_ADMIN,
                    UserRole.CHANNEL_SALES_REP,
                ],
            },
            {
                key: "payment-methods",
                label: "Payment Methods",
                path: routes.settings.paymentMethods.root,
                icon: (
                    <span className="anticon">
                        <CreditCard />
                    </span>
                ),
                allowedAccountTypes: [AccountType.CHANNEL_PARTNER, AccountType.INTERNAL],
                allowedRoles: [
                    UserRole.SUPER_ADMIN,
                    UserRole.CHANNEL_ADMIN,
                    UserRole.CHANNEL_SALES_REP,
                ],
            },
            {
                key: "tax-exemption",
                // No SUPER_ADMIN here on purpose — this page's data comes
                // from GET /tax-certificates/mine, which is CHANNEL_ADMIN /
                // CHANNEL_SALES_REP only (SUPER_ADMIN's equivalent is the
                // /cc/tax-certificates review queue).
                label: "Tax Exemption",
                path: routes.settings.taxExemption.root,
                icon: (
                    <span className="anticon">
                        <FileText />
                    </span>
                ),
                allowedAccountTypes: [AccountType.CHANNEL_PARTNER],
                allowedRoles: [
                    UserRole.CHANNEL_ADMIN,
                    UserRole.CHANNEL_SALES_REP,
                ],
            },
            {
                // Connect ChatGPT / Claude / Codex to the portal over MCP.
                key: "mcp-connections",
                label: "AI & MCP",
                path: routes.settings.mcp.root,
                icon: (
                    <span className="anticon">
                        <MessageSquareText />
                    </span>
                ),
                allowedAccountTypes: [
                    AccountType.CHANNEL_PARTNER,
                    AccountType.BRAND_AMBASSADOR,
                    AccountType.INTERNAL,
                ],
                allowedRoles: [
                    UserRole.SUPER_ADMIN,
                    UserRole.CHANNEL_ADMIN,
                    UserRole.CHANNEL_SALES_REP,
                    UserRole.BRAND_AMBASSADOR,
                ],
            },
        ],
    },
];

const commandCenterItems: CustomMenuItem[] = [
    {
        key: "cc",
        icon: <SettingOutlined />,
        label: "Command Center",
        allowedRoles: [UserRole.SUPER_ADMIN],
        children: [
            {
                key: "cc-all-orders",
                label: "All Orders",
                path: routes.comandCenter.orders.root,
                icon: (
                    <span className="anticon">
                        <ShoppingCart />
                    </span>
                ),
            },
            {
                key: "cc-shipping",
                label: "Shipping",
                path: routes.comandCenter.shipping.root,
                icon: (
                    <span className="anticon">
                        <Truck />
                    </span>
                ),
            },
            {
                key: "cc-all-users",
                label: "All Users",
                path: routes.comandCenter.users.root,
                icon: (
                    <span className="anticon">
                        <UserIcon />
                    </span>
                ),
            },
            {
                key: "cc-accounts",
                label: "Partner Accounts",
                path: routes.comandCenter.accounts.root,
                icon: (
                    <span className="anticon">
                        <Building />
                    </span>
                ),
            },
            {
                key: "cc-products",
                icon: (
                    <span className="anticon">
                        <Briefcase />
                    </span>
                ),
                label: "Products",
                path: `${routes.comandCenter.products.root}?isActive=true`,
            },
            {
                key: "cc-quotes",
                icon: (
                    <span className="anticon">
                        <FileText />
                    </span>
                ),
                label: "Quotes",
                path: routes.comandCenter.quotes.root,
            },
            {
                key: "cc-proposals",
                icon: (
                    <span className="anticon">
                        <FileText />
                    </span>
                ),
                label: "Proposals",
                path: routes.comandCenter.proposals.root,
            },
            {
                key: "cc-projects",
                icon: (
                    <span className="anticon">
                        <MapPin />
                    </span>
                ),
                label: "Projects",
                path: routes.comandCenter.projects.root,
            },
            {
                key: "cc-projects-preview",
                icon: (
                    <span className="anticon">
                        <MapPin />
                    </span>
                ),
                label: "Projects (Preview)",
                path: routes.comandCenter.projectsPreview.root,
            },
            {
                key: "cc-calendar",
                icon: <CalendarOutlined />,
                label: "Calendar",
                path: routes.comandCenter.calendar.root,
            },
            {
                key: "cc-installer-orgs",
                icon: (
                    <span className="anticon">
                        <HardHat />
                    </span>
                ),
                label: "Installer orgs",
                path: routes.comandCenter.installerOrgs.root,
            },
            {
                key: "cc-leads",
                icon: (
                    <span className="anticon">
                        <ListChecks />
                    </span>
                ),
                label: "Leads",
                path: routes.comandCenter.leads.root,
            },
            {
                key: "cc-opportunities",
                icon: (
                    <span className="anticon">
                        <BarChart3 />
                    </span>
                ),
                label: "Opportunities",
                path: routes.comandCenter.opportunities.root,
            },
            {
                key: "cc-dealer-discounts",
                icon: (
                    <PercentageOutlined />
                ),
                label: "Dealer Discounts",
                path: `${routes.comandCenter.dealerDiscounts.root}?isActive=true`,
            },
            {
                key: "cc-audit-logs",
                label: "Audit Logs",
                path: routes.comandCenter.auditLogs.root,
                icon: (
                    <span className="anticon">
                        <ClipboardList />
                    </span>
                ),
            },
            {
                key: "wallet-templates",
                label: "Wallet Templates",
                path: routes.comandCenter.walletTemplates.root,
                icon: (
                    <span className="anticon">
                        <Wallet />
                    </span>
                ),
            },
            {
                key: "cc-currency-settings",
                // Currency Settings + Bulk FX Pricing merged into one screen
                // (2026-08-11); the separate "Bulk FX Pricing" item is gone and
                // its path now redirects to this one.
                label: "Currency & Pricing",
                path: routes.comandCenter.currencySettings.root,
                icon: (
                    <span className="anticon">
                        <DollarSign />
                    </span>
                ),
            },
            {
                key: "cc-shipping-rates",
                // Distinct from the "Shipping" item above (that one is
                // shipments/labels; this one is the weight->cost rate tables),
                // hence a scale rather than a second truck.
                label: "Shipping Rates",
                path: routes.comandCenter.shippingRates.root,
                icon: (
                    <span className="anticon">
                        <Scale />
                    </span>
                ),
            },
            {
                key: "cc-tax-registrations",
                // Where we are registered to COLLECT tax (GST/HST/sales tax) —
                // the on-switch that decides whether a destination is taxed at
                // all. Sits beside Shipping Rates and Currency & Pricing because
                // all three are the money rules quoting reads; a Landmark
                // (institution/tax authority) rather than another money glyph.
                label: "Tax Registrations",
                path: routes.comandCenter.taxRegistrations.root,
                icon: (
                    <span className="anticon">
                        <Landmark />
                    </span>
                ),
            },
            {
                key: "cc-permission-flags",
                // The six ex-env-var email allowlists (mark an order paid, override
                // shipping, monthly terms, FX rates, tax registrations) turned into an
                // editable table. Role-gated to SUPER_ADMIN like the rest of this
                // group; the narrower "may EDIT" tier is PERMISSION_ADMIN_EMAILS,
                // which the menu filter cannot express (it understands roles and
                // account types only), so the page itself renders read-only for a
                // super admin who is not on that list. A key rather than a shield: it
                // grants capabilities, it is not a security dashboard.
                label: "Permission Flags",
                path: routes.comandCenter.permissionFlags.root,
                icon: (
                    <span className="anticon">
                        <KeyRound />
                    </span>
                ),
            },
            {
                key: "cc-accounting",
                icon: (
                    <span className="anticon">
                        <CreditCard />
                    </span>
                ),
                label: "Accounting",
                children: [
                    {
                        key: "cc-accounting-orders",
                        label: "Orders",
                        path: routes.comandCenter.accounting.root,
                        icon: (
                            <span className="anticon">
                                <ShoppingCart />
                            </span>
                        ),
                    },
                    {
                        key: "cc-accounting-ar-aging",
                        label: "AR Aging & Collections",
                        path: routes.comandCenter.arAging.root,
                        icon: <span className="anticon"><FileText /></span>,
                    },
                    {
                        key: "cc-accounting-invoices",
                        label: "Invoices",
                        path: routes.comandCenter.invoices.root,
                        icon: (
                            <span className="anticon">
                                <FileText />
                            </span>
                        ),
                    },
                    {
                        key: "cc-accounting-recurring",
                        label: "Recurring Transactions",
                        path: routes.comandCenter.recurringTransactions.root,
                        icon: (
                            <span className="anticon">
                                <CreditCard />
                            </span>
                        ),
                    },
                    {
                        key: "cc-accounting-end-customers",
                        label: "End Customers",
                        path: `${routes.comandCenter.accounts.root}?accountType=END_CUSTOMER`,
                        icon: (
                            <span className="anticon">
                                <Users />
                            </span>
                        ),
                    },
                    {
                        key: "cc-accounting-wallet-billing",
                        label: "Apple Wallet Billing",
                        path: routes.comandCenter.walletBilling.root,
                        icon: (
                            <span className="anticon">
                                <Wallet />
                            </span>
                        ),
                    },
                    {
                        key: "cc-accounting-units-sold",
                        label: "Units Sold",
                        path: routes.comandCenter.unitsSold.root,
                        icon: (
                            <span className="anticon">
                                <BarChart3 />
                            </span>
                        ),
                    },
                    {
                        key: "cc-accounting-free-items",
                        label: "Free Items",
                        path: routes.comandCenter.freeItems.root,
                        icon: (
                            <span className="anticon">
                                <Gift />
                            </span>
                        ),
                    },
                    {
                        key: "cc-accounting-tax-certificates",
                        // Distinct from "Tax Registrations" (where we're
                        // registered to collect tax) — this is the review
                        // queue for partner-uploaded exemption certificates
                        // that turn tax OFF for a given account/state.
                        label: "Tax Certificates",
                        path: routes.comandCenter.taxCertificates.root,
                        icon: (
                            <span className="anticon">
                                <FileText />
                            </span>
                        ),
                    },
                ],
            },
        ],
    },
];

const myWorkspaceItems: CustomMenuItem[] = [
    {
        key: "user-board",
        icon: <SettingOutlined />,
        label: "My Workspace",
        allowedRoles: [UserRole.SUPER_ADMIN],
        allowedAccountTypes: [AccountType.INTERNAL],
        children: [
            {
                key: "my-board",
                label: "Summary",
                path: routes.myWorkspace.root,
                icon: (
                    <span className="anticon">
                        <BarChart3 />
                    </span>
                ),
            },
            {
                key: "my-board-leads",
                label: "Leads",
                path: routes.myWorkspace.leads.root,
                icon: (
                    <span className="anticon">
                        <ListChecks />
                    </span>
                ),
            },
            {
                key: "my-board-opportunities",
                label: "Opportunities",
                path: routes.myWorkspace.opportunities.root,
                icon: (
                    <span className="anticon">
                        <BarChart2 />
                    </span>
                ),
            },
            {
                key: "my-board-quotes",
                label: "Quotes",
                path: routes.myWorkspace.quotes.root,
                icon: (
                    <span className="anticon">
                        <FileText />
                    </span>
                ),
            },
            {
                key: "my-board-proposals",
                label: "Proposals",
                path: routes.myWorkspace.proposals.root,
                icon: (
                    <span className="anticon">
                        <FileText />
                    </span>
                ),
            },
            {
                key: "my-board-orders",
                label: "Orders",
                path: routes.myWorkspace.orders.root,
                icon: (
                    <span className="anticon">
                        <ShoppingCart />
                    </span>
                ),
            }
        ],
    },
];

const apiAdminItems: CustomMenuItem[] = [
    {
        key: "api",
        label: "API keys",
        path: routes.api.apiKeys.root,
        icon: (
            <span className="anticon">
                <Key />
            </span>
        ),
        allowedRoles: [UserRole.API_ADMIN],
        allowedAccountTypes: [AccountType.API_PARTNER],
    },
    {
        key: "wallet-credentials",
        label: "Wallet Credentials",
        path: routes.api.walletCredentials.root,
        icon: (
            <span className="anticon">
                <Wallet />
            </span>
        ),
        allowedRoles: [UserRole.API_ADMIN],
        allowedAccountTypes: [AccountType.API_PARTNER],
    },
    {
        key: "wallet-templates",
        label: "Wallet Templates",
        path: routes.api.walletTemplates.root,
        icon: (
            <span className="anticon">
                <Wallet />
            </span>
        ),
        allowedRoles: [UserRole.API_ADMIN],
        allowedAccountTypes: [AccountType.API_PARTNER],
    },
    {
        key: "wallet-cards",
        label: "Wallet Cards",
        path: routes.api.walletCards.root,
        allowedRoles: [UserRole.API_ADMIN],
        allowedAccountTypes: [AccountType.API_PARTNER],
        icon: (
            <span className="anticon">
                <WalletCards />
            </span>
        ),
    },
    {
        key: "payment-methods",
        label: "Payment Methods",
        path: routes.api.paymentMethods.root,
        allowedRoles: [UserRole.API_ADMIN],
        allowedAccountTypes: [AccountType.API_PARTNER],
        icon: (
            <span className="anticon">
                <CreditCard />
            </span>
        ),
    },
    {
        key: "logs",
        label: "Logs",
        path: routes.api.logs.root,
        allowedRoles: [UserRole.API_ADMIN],
        allowedAccountTypes: [AccountType.API_PARTNER],
        icon: (
            <span className="anticon">
                <ClipboardList />
            </span>
        ),
    },
    {
        key: "documentation",
        label: "Documentation",
        path: routes.api.documentation.root,
        icon: (
            <span className="anticon">
                <BookOpen />
            </span>
        ),
        allowedRoles: [UserRole.API_ADMIN],
        allowedAccountTypes: [AccountType.API_PARTNER],
    },
    {
        key: "api-settings",
        label: "Settings",
        path: routes.api.settings.root,
        icon: <SettingOutlined />,
        allowedRoles: [UserRole.API_ADMIN],
        allowedAccountTypes: [AccountType.API_PARTNER],
    },
];

export const menuItems: CustomMenuItem[] = [
    ...commonNavItems,
    ...settingsItems,
    ...myWorkspaceItems,
    ...commandCenterItems,
    ...apiAdminItems,
];
